import { getGlobalVariables, getLocalDataSnapshot, useLocalData, TThemePreference } from '@libs/core';
import { DefaultTheme, LocalDataKeys } from '@libs/types';
import { ReactNode, useEffect, useState } from 'react';

const isDarkThemeDisabled = () => {
    const { disableDarkTheme } = getGlobalVariables('disableDarkTheme');
    return disableDarkTheme === 'true';
};

/** OS-level color scheme (respecting the `disableDarkTheme` kill-switch). */
export const getSystemTheme = () => {
    if (isDarkThemeDisabled()) {
        return DefaultTheme.light;
    }
    let theme = DefaultTheme.light;
    if (window?.matchMedia) {
        const isMatchesDark = window.matchMedia('(prefers-color-scheme: dark)')?.matches;
        theme = isMatchesDark ? DefaultTheme.dark : DefaultTheme.light;
    }
    return theme;
};

/**
 * Resolves the effective theme: the `disableDarkTheme` kill-switch wins, then an explicit
 * `light`/`dark` user preference (saved via `save_local_data:theme`), otherwise (`system`
 * or no preference) the OS color scheme.
 */
const resolveTheme = (preference: TThemePreference | null, systemTheme: string) => {
    if (isDarkThemeDisabled()) {
        return DefaultTheme.light;
    }
    if (preference === DefaultTheme.light || preference === DefaultTheme.dark) {
        return preference;
    }
    return systemTheme;
};

export const getCurrentThemeForText = () => resolveTheme(getLocalDataSnapshot<TThemePreference>(LocalDataKeys.theme), getSystemTheme());

const useThemeDetector = () => {
    const preference = useLocalData<TThemePreference>(LocalDataKeys.theme);
    const [systemTheme, setSystemTheme] = useState(getSystemTheme);

    // Track the OS color scheme; it only matters when the preference is `system`/unset,
    // but keeping it current means switching the preference back to `system` reacts instantly.
    useEffect(() => {
        if (isDarkThemeDisabled()) {
            return;
        }
        const mediaQueryList = window?.matchMedia('(prefers-color-scheme: dark)');
        const handler = () => setSystemTheme(getSystemTheme());
        mediaQueryList?.addEventListener('change', handler);
        return () => {
            mediaQueryList?.removeEventListener('change', handler);
        };
    }, []);

    const theme = resolveTheme(preference, systemTheme);

    // Apply the resolved theme to the DOM/storage whenever it changes (preference or OS).
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
    }, [theme]);

    return theme;
};

interface ProviderProps {
    children: ReactNode;
}

const ConsumerThemeProvider = ({ children }: ProviderProps) => {
    useThemeDetector();

    return children;
};
export { ConsumerThemeProvider, useThemeDetector };
